import { LOCATION_SETTINGS_CONFIG, type LocationType } from "api-types/builders/builderTypes";
import type { ClientCommunityWithLevel } from "api-types/communities/communityBaseTypes";
import { getAvailableLocationTypes, getLocationTypeLabel } from "api-types/locations/locationDisplay";
import type { LocationLevelType } from "api-types/locations/locationLevelTypes";
import type { FeatureSet } from "common/constants/features";
import { newLocationPath } from "lib/routeHelpers";
import { useUserContext } from "lib/state/UserContextProvider";
import Button from "ui/buttons/Button";
import { CustomButtonDropdown } from "ui/components/DropdownMenu";
import Link from "ui/components/Link";
import IconMiniChevronDown from "ui/icons/IconMiniChevronDown";
import styles from "./NewLocationButton.module.scss";

const PERMISSION_MESSAGE = "Only users with the Admin or Manager role can create a new Location.";

/**
 * Shared availability predicate for both the dropdown button and the menu items, so the two
 * surfaces can't silently disagree on which location types are offered for a given parent.
 */
function isLocationTypeAvailable(
  type: LocationLevelType,
  parentType: LocationLevelType | undefined,
  enabledFeatures: FeatureSet,
): boolean {
  if (type === "productline") {
    return enabledFeatures.configProductLine ?? false;
  }
  if (type !== "multiunit") {
    return true;
  }
  // Multi-unit requires configTownhomes FF and a valid parent per LOCATION_SETTINGS_CONFIG
  const restrictedParentTypes = LOCATION_SETTINGS_CONFIG.multiunit.restrictedParentTypes;
  return (
    (enabledFeatures.configTownhomes ?? false) &&
    (!restrictedParentTypes || restrictedParentTypes.includes(parentType as LocationType))
  );
}

interface NewLocationButtonProps {
  builderId: string;
  buttonText?: string;
  parentLocation?: ClientCommunityWithLevel;
}
export default function NewLocationButton({ builderId, buttonText, parentLocation }: NewLocationButtonProps) {
  const { hasPermission } = useUserContext();
  const canEditCommunities = parentLocation
    ? hasPermission("EDIT_COMMUNITIES", { resourceId: parentLocation.id })
    : hasPermission("EDIT_COMMUNITIES");

  return (
    <LocationDropdown
      builderId={builderId}
      hasPermission={canEditCommunities}
      buttonText={buttonText}
      parentLocation={parentLocation}
    />
  );
}

interface LocationDropdownProps {
  hasPermission: boolean;
  builderId: string;
  buttonText?: string;
  parentLocation?: ClientCommunityWithLevel;
}

function LocationDropdown({ hasPermission, builderId, buttonText, parentLocation }: LocationDropdownProps) {
  const { builder, enabledFeatures } = useUserContext();
  const parentType = parentLocation?.locationLevel?.type;
  const filteredLevels = getAvailableLocationTypes(parentType, builder?.locationSettings).filter((type) =>
    isLocationTypeAvailable(type, parentType, enabledFeatures),
  );

  return (
    <CustomButtonDropdown
      menuItems={<LocationLevelMenuItems builderId={builderId} parentLocation={parentLocation} />}
      customButton={
        <Button
          aria-label="Add new location"
          btnStyle="primary"
          btnSize="large"
          disabled={!hasPermission || filteredLevels.length === 0}
          title={PERMISSION_MESSAGE}
        >
          {buttonText ?? "Add"}
          <IconMiniChevronDown className={styles.miniChevron} />
        </Button>
      }
    />
  );
}

interface LocationLevelMenuItemsProps {
  builderId: string;
  parentLocation?: ClientCommunityWithLevel;
}

export function LocationLevelMenuItems({ builderId, parentLocation }: LocationLevelMenuItemsProps) {
  const { builder, enabledFeatures } = useUserContext();
  const parentType = parentLocation?.locationLevel?.type;
  const filteredLevels = getAvailableLocationTypes(parentType, builder?.locationSettings).filter((type) =>
    isLocationTypeAvailable(type, parentType, enabledFeatures),
  );

  return (
    <>
      {filteredLevels.map((l) => {
        const label = getLocationTypeLabel(l, builder?.locationSettings);
        return (
          <Link
            aria-label={`Add new ${label.toLowerCase()}`}
            key={`location-type-dropdown-${l}`}
            href={newLocationPath({
              builderId,
              locationLevel: l,
              redirectTo: encodeURIComponent(window.location.pathname),
              parentLocationId: parentLocation?.id,
            })}
          >
            {label}
          </Link>
        );
      })}
    </>
  );
}
